/* eslint-disable no-prototype-builtins */
import { SceneComponent } from '@/_models/virtualStaging/Scene.component';
import { LoadingState } from '@/_models/virtualStaging/VSObject';
import { Inputs, Outputs } from './types';
import { getCircleShaderMaterial } from '@/_models/virtualStaging/materials/CircleShaderMaterial';
import { normalizeDimensions } from '@/utils/normalizeDimensions';

abstract class AbstractAvatar extends SceneComponent {
  public id: string = null;

  public root: any = null;

  public pivot: any = null;

  public label: any = null;

  public labelSize = [0, 0];

  public model: any = null;

  inputs: Inputs = {
    color: 0xff0033,
    label: 'User',
    bodyType: null,
    url: null,
    uuid: null,
    visible: true,
    sweep: null,
  };

  outputs = {
    loadingState: LoadingState.Idle,
  } as Outputs;

  public getPosition = () => this.model.position;

  protected initRoot() {
    const THREE = this.context.three;
    this.root = new THREE.Object3D();
    this.pivot = new THREE.Object3D();
    this.root.add(this.pivot);

    this.outputs.objectRoot = this.root;
  }

  protected initLabel() {
    this.label = this.makeLabel(this.inputs.label, {
      fontsize: 48,
      textColor: {
        r: 255,
        g: 255,
        b: 255,
        a: 1.0,
      },
      borderColor: {
        r: 0,
        g: 0,
        b: 0,
        a: 0,
      },
      backgroundColor: {
        r: 0,
        g: 0,
        b: 0,
        a: 0.7,
      },
      borderThickness: 16,
    });

    if (this.inputs.bodyType === 'fullbody') {
      this.label.rotateX(Math.PI / 2);
      this.label.position.setY(-0.05);
      this.label.position.setZ(2.3);
      this.pivot.add(this.label);
    } else {
      this.label.position.setY(0.83);
      this.pivot.add(this.label);
    }

    if (this.root && this.root.parent) {
      this.root.parent.visible = this.inputs.visible;
    }
  }

  public getLabelSize() {
    return this.labelSize;
  }

  protected makeLabel(message, parameters) {
    const THREE = this.context.three;
    if (parameters === undefined) parameters = {};

    const fontface = parameters.hasOwnProperty('fontface') ? parameters.fontface : 'Arial';
    const fontsize = parameters.hasOwnProperty('fontsize') ? parameters.fontsize : 18;
    const borderThickness = parameters.hasOwnProperty('borderThickness')
      ? parameters.borderThickness
      : 4;
    const borderColor = parameters.hasOwnProperty('borderColor')
      ? parameters.borderColor
      : {
          r: 0,
          g: 0,
          b: 0,
          a: 1.0,
        };
    const backgroundColor = parameters.hasOwnProperty('backgroundColor')
      ? parameters.backgroundColor
      : {
          r: 255,
          g: 255,
          b: 255,
          a: 1.0,
        };
    const textColor = parameters.hasOwnProperty('textColor')
      ? parameters.textColor
      : {
          r: 255,
          g: 255,
          b: 255,
          a: 1.0,
        };

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = `Bold ${fontsize}px ${fontface}`;
    const metrics = context.measureText(message);
    const textWidth = metrics.width;

    context.fillStyle = `rgba(${backgroundColor.r},${backgroundColor.g},${backgroundColor.b},${backgroundColor.a})`;
    context.strokeStyle = `rgba(${borderColor.r},${borderColor.g},${borderColor.b},${borderColor.a})`;

    context.lineWidth = borderThickness;
    const rectW = (textWidth + borderThickness) * 1.1;
    const rectH = fontsize * 1.4 + borderThickness;
    this.roundRect(context, borderThickness / 2, borderThickness / 2, rectW, rectH, 8);

    context.fillStyle = `rgba(${textColor.r}, ${textColor.g}, ${textColor.b}, 1.0)`;
    context.fillText(message, borderThickness, fontsize + borderThickness);

    // crop exactly the rectangle size
    const label = this.crop(canvas, 0, 0, rectW + borderThickness, rectH + borderThickness);

    const texture = new THREE.Texture(label);
    texture.needsUpdate = true;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.encoding = THREE.sRGBEncoding;
    texture.repeat.set(1, 1);

    const planeWidth = label.width / 1000; // scaling down
    const planeHeight = label.height / 1000;

    // need for dynamic mute button positioning
    this.labelSize = [planeWidth, planeHeight];

    const plane = this.makePlane(planeWidth, planeHeight, texture);
    plane.scale.set(1, 1, 1);
    return plane;
  }

  public makeAvatarImageRound(str: string) {
    if (!str) {
      console.error('Invalid name');
      return;
    }
    const THREE = this.context.three;
    const colors = [
      { bg: 'red', text: { r: 255, g: 255, b: 255, a: 1.0 } },
      { bg: 'green', text: { r: 255, g: 255, b: 255, a: 1.0 } },
      { bg: 'blue', text: { r: 255, g: 255, b: 255, a: 1.0 } },
      { bg: 'pink', text: { r: 255, g: 255, b: 255, a: 1.0 } },
      { bg: 'orange', text: { r: 0, g: 0, b: 0, a: 1.0 } },
      { bg: 'white', text: { r: 0, g: 0, b: 0, a: 1.0 } },
      { bg: 'grey', text: { r: 255, g: 255, b: 255, a: 1.0 } },
    ];
    const letter = str[0].toUpperCase();
    const asciiValue = letter.charCodeAt(0);
    const colorIndex = asciiValue % colors.length;
    const letterObject = this.makeLabel(letter, {
      fontsize: 48,
      textColor: colors[colorIndex].text,
      borderColor: {
        r: 0,
        g: 0,
        b: 0,
        a: 0,
      },
      backgroundColor: {
        r: 255,
        g: 255,
        b: 255,
        a: 0,
      },
      borderThickness: 0,
      shape: 'round',
    });
    letterObject.scale.setScalar(7);

    const geometry = new THREE.CircleGeometry(0.5, 32);
    const material = new THREE.MeshBasicMaterial({ color: colors[colorIndex].bg });
    const backgroundCircle = new THREE.Mesh(geometry, material);

    const avatarImage = new THREE.Group();
    avatarImage.add(backgroundCircle);
    avatarImage.add(letterObject);

    letterObject.position.z = 0.05;

    return avatarImage;
  }

  // function for drawing rounded rectangles
  // eslint-disable-next-line class-methods-use-this
  private roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }

  private makePlane(width, height, texture) {
    const THREE = this.context.three;
    const geometry = new THREE.PlaneGeometry(width, height, 10, 10);

    const material = new THREE.MeshBasicMaterial({
      map: texture,
      side: THREE.DoubleSide,
      blending: THREE.NormalBlending,
      transparent: true,
    });
    return new THREE.Mesh(geometry, material);
  }

  private makeRoundPlane(texture) {
    // uncomment if we need adaptive border color
    // const { r, g, b } = getAverageRGB(texture.image);
    // const luma = parseInt(`${getBrightness(r, g, b)}`, 10);

    const THREE = this.context.three;
    const { width, height } = normalizeDimensions(texture.image.width, texture.image.height);
    const radius = Math.min(width, height) / 2;
    const material = getCircleShaderMaterial(texture, radius, width, height);

    const geometry = new THREE.PlaneGeometry(width, height);

    const outerCircleGeometry = new THREE.CircleGeometry(Math.min(width, height) / 2, 64);
    const outerCircleMaterial = new THREE.MeshBasicMaterial({ color: 'white' });
    const outerCircle = new THREE.Mesh(outerCircleGeometry, outerCircleMaterial);
    const image = new THREE.Mesh(geometry, material);

    const group = new THREE.Group();
    group.add(outerCircle);
    group.add(image);
    image.position.z = 0.005;
    image.scale.setScalar(0.92);

    return group;
  }

  // eslint-disable-next-line class-methods-use-this
  private crop(can, x, y, w, h) {
    // get your canvas and a context for it
    const ctx = can.getContext('2d');

    // get the image data you want to keep.
    const imageData = ctx.getImageData(x, y, w * 2, h);

    // create a new cavnas same as clipped size and a context
    const newCan = document.createElement('canvas');
    newCan.width = w - x;
    newCan.height = h - y;
    const newCtx = newCan.getContext('2d');

    // put the clipped image on the new canvas.
    newCtx.putImageData(imageData, 0, 0);

    return newCan;
  }

  private loadAsync = async (THREE, url: string) => {
    if (url.includes('.gltf') || url.includes('.glb'))
      return new Promise<any>((resolve, reject) => {
        const loader = new THREE.GLTFLoader();
        loader.load(
          url,
          (gltf) => resolve(gltf),
          () => null,
          (err) => reject(err),
        );
      });
    if (url.includes('.fbx'))
      return new Promise<any>((resolve, reject) => {
        const loader = new THREE.FBXLoader();
        loader.load(
          url,
          (model) => resolve(model),
          () => null,
          (err) => reject(err),
        );
      });
    if (url.includes('.png') || url.includes('.jpg') || url.includes('base64'))
      return new Promise<any>((resolve, reject) => {
        const loader = new THREE.TextureLoader();
        loader.load(
          url,
          (texture) => resolve(texture),
          () => null,
          (err) => reject(err),
        );
      });
  };

  protected async loadAnimation(THREE, url: string) {
    return this.loadAsync(THREE, url);
  }

  protected async loadModel(THREE, url: string) {
    const modelData = await this.loadAsync(THREE, url);

    if (modelData.isTexture) {
      return this.makeRoundPlane(modelData);
    } else {
      modelData.scene.position.set(0, 0, 0);
      return modelData.scene;
    }
  }
}

export default AbstractAvatar;
